import React from 'react';
import { useRouter } from 'next/router';

import { AccountMenu } from 'components/account';
import {
  Avatar, Button, Link, Popover, PopoverContent
} from 'components/ui';
import IconChevronDown from 'components/ui/icons/IconChevronDown';
import { APP_ROUTES } from 'constants/routes';
import { checkIfMobile } from 'helpers/BrowserHelpers';
import { cx } from 'helpers/StylingHelpers';
import { useTranslation } from 'hooks/common/useTranslation';
import { useIsOpen } from 'hooks/useIsOpen';

import LoginPanel from '../LoginPanel';

import type { IUser } from 'types';

import styles from './UserPopoverDesktop.module.scss';

interface Props {
  className?: string,
  buttonClassName?: string,
  //
  user?: IUser,
  padding?: number
}

const UserPopoverDesktop = (props: Props) => {

  const {
    className,
    buttonClassName,
    //
    user,
    padding,
  } = props;

  // Refs

  const ref = React.useRef<HTMLDivElement>(null);

  // Hooks

  const router = useRouter();
  const { t } = useTranslation();

  const { isOpen, toggle, close } = useIsOpen();

  // Effects

  const handleRouteChange = () => {
    close();
  };

  React.useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  // Content

  const Content = user
    ? <LoggedIn />
    : <LoginPanel />;

  // Trigger

  const userButtonLabel = user ? user.fullName : t('NAVIGATION.ACCOUNT.TITLE');
  const isMobile = checkIfMobile();

  const Trigger = (
    isMobile
      ? (
        <UserPopoverButton
          className={buttonClassName}
          label={userButtonLabel}
          user={user}
          tag="button"
          onClick={toggle}
        />
      ) : (
        <Link href={APP_ROUTES.ACCOUNT} prefetch={false} passHref>
          <UserPopoverButton
            className={buttonClassName}
            label={userButtonLabel}
            user={user}
            tag="a"
          />
        </Link>
      )
  );

  // Render

  return (
    <div
      ref={ref}
      className={styles.root}
    >
      {/* TODO: Check types for ref */}
      <Popover
        parentElement={ref as React.MutableRefObject<HTMLElement>}
        isOpen={isMobile ? isOpen : false}
        persist
        //
        className={cx(styles.popover, className)}
        contentClassName={cx(styles.popover, { [styles.popoverLoggedIn]: !!user })}
        //
        align="end"
        positions={['bottom']}
        padding={padding}
        //
        withArrow
        withHover={!isMobile}
        //
        content={Content}
        //
        onClickOutside={() => {
          if (isMobile) {
            close();
          }
        }}
      >
        {Trigger}
      </Popover>
    </div>
  );
};

// Components

interface UserPopoverButtonProps {
  tag: 'a' | 'button',
  label?: string,
  className?: string,
  user?: IUser,
  onClick?: () => void
}

const UserPopoverButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, UserPopoverButtonProps>((props, ref) => {
  const {
    className,
    tag,
    label,
    user,
    onClick,
  } = props;

  // Render

  return (
    <Button
      ref={ref}
      tag={tag}
      size="medium"
      className={cx(
        styles.btn,
        className
      )}
      beforeContent={(
        <Avatar
          user={user}
          variant="empty"
          size={32}
          iconSize={22}
          iconStrokeWidth={1.5}
          withGeniusBadge
          geniusBadgeProps={{
            withIcon: true,
            className: styles.geniusBadge
          }}
        />
      )}
      endIcon={IconChevronDown}
      endIconProps={{ size: 16, strokeWidth: 2 }}
      onClick={onClick}
      data-testid="header.user-name"
    >
      <span className={styles.userButtonContent}>
        {label}
      </span>
    </Button>
  );
});

const LoggedIn = () => {

  // Render

  return (
    <PopoverContent
      className={styles.popoverContent}
      title="NAVIGATION.ACCOUNT.LOGGED_IN.TITLE"
    >
      <AccountMenu
        className={styles.accountMenu}
        spotlightClassName={styles.spotlighted}
        menuProps={{
          className: styles.menu,
          itemClassName: styles.item,
          linkClassName: styles.link,
        }}
        //
        isDesktop
      />
    </PopoverContent>
  );
};

// Export

export default React.memo(UserPopoverDesktop);
